import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Permissions,
  PermissionService,
} from '@app/core/services/permission.service';
import { SessionService } from '@app/security/session.service';
import { LoadingMaskService } from '@app/core/services/loading-mask.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-safe-redirect',
  templateUrl: './safe-redirect.component.html',
  styleUrls: ['./safe-redirect.component.scss'],
  standalone: true,
})
export class SafeRedirectComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private ps: PermissionService,
    private loadingMask: LoadingMaskService,
    private sessionService: SessionService,
  ) { }

  ngOnInit() {
    this.loadingMask.loadingOn();

    if (this.ps.hasCurrentUser()) {
      this.navigateAway();
      return;
    }

    // Always ask the server who this is, never localStorage.
    //
    // This used to branch on hasUserInLocalStorage() and, when set, take the
    // identity from the stored id/clientId. A browser still holding a previous
    // user's localStorage therefore loaded that user's permissions, the route
    // guard rejected the target route, the guard sent the browser back here,
    // and it resolved to the same wrong identity again -- an infinite redirect
    // loop with no server-side cause, which is why it cleared in a fresh
    // profile and never reproduced in incognito. Invitees hit it hardest,
    // being the people most likely to still be carrying a prior identity.
    //
    // getSessionUser() is a strict superset of the old path: it resolves the
    // client and rewrites localStorage from the session (setUpUser), so the
    // stale copy is corrected rather than trusted.
    this.trySessionUserOrLogout();
  }

  private trySessionUserOrLogout() {
    this.sessionService.getSessionUser().subscribe({
      next: () => {
        this.navigateAway();
      },
      error: () => {
        // Any getSessionUser() failure lands here (no session, expired
        // session, or a backend error) -- see buildLoginRedirectFromCurrentRoute()
        // for why routing straight to /login is safe in every environment.
        this.router.navigateByUrl(
          this.sessionService.buildLoginRedirectFromCurrentRoute(),
          { replaceUrl: true },
        );
      },
    });
  }

  ngOnDestroy() {
    this.loadingMask.loadingOff();
  }

  navigateAway() {
    // Reaching here means a login actually succeeded, so the loop-breaker
    // LoginComponent set on its way out has done its job. Nothing else clears
    // it on success -- a successful Auth0 login lands here, never back on
    // /login -- so without this the marker outlives the login and the next
    // /login load in this tab renders the legacy form once instead of
    // redirecting to Auth0.
    this.sessionService.clearBackendLoginAttempt();

    const snapshot = this.activeRoute.snapshot;

    if (snapshot.queryParams.hasOwnProperty('redirect')) {
      this.goToRedirect(snapshot);
    } else if (this.ps.checkPermissions(Permissions.ui_journey_list)) {
      this.router.navigateByUrl('/experiences', { replaceUrl: true });
    } else if (this.ps.checkPermissions(Permissions.ui_quick_launch)) {
      this.router.navigateByUrl('/onboarding', { replaceUrl: true });
    } else if (this.ps.checkPermissions(Permissions.ui_journey_launcher)) {
      this.router.navigateByUrl('/launcher', { replaceUrl: true });
    } else if (this.ps.checkPermissions(Permissions.ui_user_mgmt)) {
      this.router.navigateByUrl('/user-mgmt', { replaceUrl: true });
    }
  }

  goToRedirect(snapshot) {
    // two-way takes full path, all other routes do not.
    const redirectUrl = this.isTwoWay(snapshot.queryParams.redirect)
      ? snapshot.queryParams.redirect
      : snapshot.queryParams.redirect.split('/')[1];
    this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
  }

  isTwoWay(url): boolean {
    return _.includes(url, 'two-way');
  }
}
